import styled from "styled-components";
import Hero from "src/components/Hero/Hero";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import EventFinished from "src/components/EventFinished";
import SubTitleSection from "src/components/SubTitleSection/SubTitleSection";
import TitleSection from "src/components/TitleSection/TitleSection";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";

const MscCruiseApril2026 = () => {
  const { t, i18n } = useTranslation();
  const detailsBgImage = useResponsiveImageUrl(
    "promotions/gaming-machines/msc-cruise-april-2026/msc-image-parallax.png",
  );

  return (
    <Div>
      <Hero
        bgImage="promotions/gaming-machines/msc-cruise-april-2026/msc-hero-image-2400x800px.jpg"
        image={`promotions/gaming-machines/msc-cruise-april-2026/msc-logo-2400x2400px-${i18n.language}.png`}
        imageAlt={t("mscCruiseApril2026.hero.logoAlt")}
        title={t("mscCruiseApril2026.content.title")}
        description={t("mscCruiseApril2026.content.subtitle")}
        descriptionBgColor="#0c1d2f"
        className="msc-hero"
        fluidImage
      />
      <EventFinished
        scheduleKeyName="mscCruiseApril2026"
        eventType="promotion"
      />
      <MainContentDiv>
        <container-div>
          <TitleSection
            title="mscCruiseApril2026.content.sectionTitle"
            text={t("mscCruiseApril2026.content.sectionText")}
            borderColor="var(--red)"
            dark={true}
            as="h2"
            className="escape-section"
          />
        </container-div>
        <DetailsSection bgImage={detailsBgImage}>
          <div className="details-inner">
            <div className="details-image">
              <ResponsiveImage
                src="promotions/gaming-machines/msc-cruise-april-2026/msc-image-2.png"
                alt={t("mscCruiseApril2026.content.detailsTitle")}
              />
            </div>
            <div className="details-card">
              <SubTitleSection
                subTitle={t("mscCruiseApril2026.content.detailsTitle")}
                borderColor="var(--red)"
                dark={true}
                as="h2"
                className="details-title"
              />
              <div className="details-grid">
                <div>
                  <span className="label">
                    {t("mscCruiseApril2026.content.details.whereLabel")}
                  </span>
                  <p className="where">{t("mscCruiseApril2026.content.details.whereText")}</p>
                </div>
                <div>
                  <span className="label">
                    {t("mscCruiseApril2026.content.details.whenLabel")}
                  </span>
                  <ReactMarkdown>
                    {t("mscCruiseApril2026.content.details.whenText")}
                  </ReactMarkdown>
                </div>
                <div>
                  <span className="label">
                    {t("mscCruiseApril2026.content.details.timeLabel")}
                  </span>
                  <p>{t("mscCruiseApril2026.content.details.timeText")}</p>
                </div>
              </div>
            </div>
          </div>
        </DetailsSection>
        <container-div>
          <ReactMarkdown className="rsvp-note">
            {t("mscCruiseApril2026.content.rsvp")}
          </ReactMarkdown>
        </container-div>
      </MainContentDiv>
    </Div>
  );
};

const Div = styled.div`
  width: 100%;
  height: auto;
  .hero_container {
    margin-bottom: 120px;
    overflow: visible;
  }

  .msc-hero {
    .hero_image_container {
      padding: 20px 20px 40px;
      min-height: 800px;
      background-position: top;
    }

    .hero_image {
      max-width: 820px;
      width: 100%;
    }

    .hero_text_container {
      position: relative;
      bottom: -120px;
      background-color: #0c1d2f;
      backdrop-filter: none;
      padding: 40px 20px;
      color: var(--white);
      z-index: 10;
    }

    .hero_text_container h1 {
      margin-bottom: 20px;
    }

    .hero_text_container p {
      font-size: 1rem;
      margin: 0;
    }
  }
  .escape-section {
    max-width: 880px;
    margin: 0 auto;
    p {
      font-size: 1.2rem;
      margin-top: 1rem;
    }
  }
  .escape-section strong {
    color: #1d5c8b;
  }

  .rsvp-note {
    margin: 2rem auto;
    text-align: center;
    font-size: 1.05rem;
    color: #5b5b5b;
  }

  .rsvp-note a,
  strong {
    color: #1d5c8b;
    text-decoration: none;
  }

  .rsvp-note a:hover,
  .rsvp-note a:focus {
    text-decoration: underline;
  }
  @media (max-width: 992px) {
    .hero_container {
      margin-bottom: 180px;
    }
    .msc-hero {
      .hero_image_container {
        min-height: unset;
      }
      .hero_text_container {
        bottom: -180px;
      }
    }
  }
  @media (min-width: 1896px) {
    .rsvp-note {
      margin-top: 2rem;
    }
  }
`;

const MainContentDiv = styled.div`
  background-color: var(--white);
  width: 100%;
`;

const DetailsSection = styled.section`
  background: url(${(props) => props.bgImage}) center / cover no-repeat;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  background-color: #0c1d2f;
  padding: 2.5rem 1.5rem;
  width: 100%;

  .details-inner {
    display: grid;
    gap: 1.5rem;
    align-items: center;
    max-width: 1200px;
    margin: 0 auto;
    grid-template-columns: minmax(0, 1fr);
  }

  .details-card {
    color: var(--white);
    padding: 1.5rem 1.75rem;
    border-radius: 0;
    backdrop-filter: none;
    width: 100%;
    max-width: 100%;
    height: auto;
  }

  .details-title {
    padding: 0 0 1rem;
  }

  .details-title h3 {
    font-weight: 800;
    font-size: 1.4rem;
    color: var(--white);
  }

  .details-title h3:before {
    border-bottom-color: rgba(255, 255, 255, 0.2);
  }

  .details-grid {
    display: grid;
    gap: 1.4rem;
    grid-template-columns: 1fr;
  }

  .details-grid p {
    margin: 0.35rem 0 0;
    font-size: clamp(1.1rem, 2.2vw, 1.35rem);
    line-height: 1.35;
  }
  .where {
    white-space: normal;
  }

  .label {
    font-weight: 700;
    text-transform: uppercase;
    font-size: 0.9rem;
    letter-spacing: 0.08em;
    color: #a7d0f0;
  }

  .details-image img {
    /* position: relative;
    top: -50px;
    width: 100%; */
    max-width: 540px;
    margin: 0 auto;
  }

  @media (min-width: 1896px) {
    background-size: cover;
    .details-image img {
      max-width: 600px;
    }
  }
  @media (min-width: 992px) {
    .details-inner {
      grid-template-columns: 1.1fr 0.9fr;
    }

    .details-image {
      display: flex;
      justify-content: center;
    }
  }
  @media (max-width: 992px) {
    background-position: top;
    .details-card {
      max-width: 100%;
      order: -1;
      padding: 1.25rem 0.5rem;
    }

    .details-grid {
      gap: 1.15rem;
    }

    .details-grid p {
      font-size: clamp(1.2rem, 4vw, 1.5rem);
    }
  }
`;

export default MscCruiseApril2026;
